import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Button, Result, Spin } from 'antd';
import styled from 'styled-components';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Transaction } from '~/types';
import { getTransactionById } from '~/api/transactions';
import useTimeout from '~/hooks/useTimeout';

const refetchInterval = 3 * 1000;
const resultScreenDelay = 1 * 1000;
const nextScreenInterval = 10 * 1000;

interface Props {
  transactionId: string;
  completeComponent: ReactNode;
  onComplete: (t: Transaction) => void;
  fullWidth?: boolean;
}

const TransactionProcessing: FC<Props> = (props) => {
  const { transactionId, onComplete, completeComponent, fullWidth } = props;
  const [t] = useTranslation('transactions');

  const [isTransactionQueryEnabled, setTransactionQueryEnabled] = useState(true);
  const transactionQuery = useQuery(
    ['transactionStatus', transactionId],
    () => getTransactionById(transactionId),
    {
      refetchInterval,
      enabled: isTransactionQueryEnabled,
    }
  );
  const transactionStatus = transactionQuery.data?.data.status;

  const onClose = () => {
    setTransactionQueryEnabled(false);
  };

  const onCompleteRef = useRef<() => void>();
  onCompleteRef.current = () => {
    onComplete(transactionQuery.data?.data!);
  };

  const [goFurther, setGoFurther] = useState(false);
  useTimeout(() => setGoFurther(true), nextScreenInterval);

  useEffect(() => {
    if (
      // is completed or rejected
      (transactionStatus &&
        transactionStatus !== 'PENDING' &&
        transactionStatus !== 'PROCESSING') ||
      // timout
      goFurther
    ) {
      setTimeout(() => {
        setTransactionQueryEnabled(false);
      }, resultScreenDelay);
      onCompleteRef.current && onCompleteRef.current();
    }
  }, [transactionStatus, setTransactionQueryEnabled, onCompleteRef, goFurther]);

  if (!isTransactionQueryEnabled)
    return (
      <AnimationInContainer style={fullWidth ? { width: '100%' } : {}}>
        {completeComponent}
      </AnimationInContainer>
    );

  return (
    <Result
      title={t('Transfer is processing')}
      subTitle={t('Please wait...')}
      icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 72 }} spin />} />}
      extra={
        <Button type="default" onClick={onClose}>
          {t('translation:close')}
        </Button>
      }
    />
  );
};

const AnimationInContainer = styled.div`
  animation: inAnimation 250ms ease-in;
  @keyframes inAnimation {
    0% {
      opacity: 0;
      visibility: hidden;
    }
    100% {
      opacity: 1;
      visibility: visible;
    }
  }
`;

export default TransactionProcessing;
